import React, { Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VaultProvider } from './contexts/VaultContext';
import Background from './components/Background';
import LandingPage from './components/LandingPage';
import { BUILD_ID } from './buildInfo';
import './index.css';

/**
 * ⚠️ LA COUPURE EST A LA FRONTIERE D'AUTHENTIFICATION, PAS PAR ROUTE.
 *
 * Tout ce qui sert a un membre connecte — les providers wallet et les pages — vit dans un
 * morceau separe. Un visiteur sans compte ne voit que la page d'accueil, qui n'utilise ni viem,
 * ni Solana, ni RainbowKit : il n'a plus a les telecharger pour rien.
 *
 * Couper par route aurait aussi allege les membres connectes, mais aurait fait vivre les
 * providers wallet dans le sous-arbre du swap : en sortir les demonte, donc reconnexion et
 * perte de l'etat en vol. Ici ils sont montes une fois, au-dessus de toutes les pages, et n'en
 * bougent plus — le comportement est exactement celui d'avant.
 */
const AuthedApp = React.lazy(() => import('./AuthedApp'));

/**
 * ⚠️ LE PRECHARGEMENT N'EST PAS UN CONFORT, IL PAIE LA COUPURE.
 *
 * `React.lazy` ne telecharge qu'au moment ou le composant est rendu. Sans ceci, un membre deja
 * connecte attendrait `/api/auth/me` PUIS le morceau, en serie — on lui aurait ajoute un
 * aller-retour. Lance ici, le telechargement part en parallele de cet appel, qui bloque deja :
 * le morceau est la avant que la reponse arrive, et le membre ne voit aucune difference.
 *
 * Sur une session non authentifiee c'est un telechargement d'avance, pas un gaspillage : la
 * page d'accueil n'a qu'une issue, se connecter ou s'inscrire.
 */
function usePrefetchAuthedApp() {
  useEffect(() => { import('./AuthedApp').catch(() => { /* le rendu reessaiera */ }); }, []);
}

/**
 * ⚠️ L'APP N'A QU'UN SEUL CHEMIN : `/`.
 *
 * Les pages sont de l'etat React, pas des URLs — il n'y a pas de routeur. Un chemin autre que `/`
 * ne change donc rien a ce qui s'affiche : il MENT, simplement.
 *
 * Et un chemin s'y installait : X renvoie le navigateur sur `X_RETURN_URL`, le code nettoyait la
 * requete (`?x=linked&…`) mais gardait le chemin. Resultat, apres un seul rattachement de compte X
 * la barre d'adresse restait bloquee sur `/rewards`, rafraichissement compris, en affichant la
 * page de swap. Rien de casse, mais une adresse qu'on ne peut ni relire ni partager.
 *
 * ⚠️ La REQUETE est conservee : c'est elle qui porte le resultat du retour de X, lu plus bas dans
 * `AuthedApp`. La normaliser ici avalerait le message avant qu'il soit affiche.
 *
 * ⚠️ Le jour ou on ajoute un vrai routeur, CETTE FONCTION DOIT PARTIR — elle se battrait avec lui.
 */
function useSinglePath() {
  useEffect(() => {
    if (window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/' + window.location.search + window.location.hash);
    }
  }, []);
}

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen relative overflow-hidden">
    <Background />
    <div className="relative z-10">{children}</div>
  </div>
);

const LoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <img src="/logo.webp" alt="Nuxaris" className="w-16 h-16 animate-pulse" />
  </div>
);

const Gate: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  useSinglePath();
  usePrefetchAuthedApp();

  if (authLoading) return <Shell><LoadingScreen /></Shell>;
  if (!user) return <Shell><LandingPage onNavigate={() => {}} /></Shell>;

  // Le repli reprend l'ecran d'attente deja affiche pendant l'appel d'authentification : rien
  // ne bouge a l'ecran entre les deux, meme si le morceau n'etait pas encore arrive.
  return (
    <Suspense fallback={<Shell><LoadingScreen /></Shell>}>
      <AuthedApp />
    </Suspense>
  );
};

// Console trace: tells at a glance which version is actually running in the browser,
// cache included.
console.info(`Nuxaris build ${BUILD_ID}`);

const App: React.FC = () => (
  <AuthProvider>
    <VaultProvider>
      <Gate />
    </VaultProvider>
  </AuthProvider>
);

export default App;
